import { useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { useSetState } from "@mantine/hooks";
import { message } from "antd";
import axios from "axios";
import UserContext from "../../../context/context";
import CaseStudyHero from "../../../components/KnowledgeHub/CaseStudy/Hero";
import CaseStudyTestimonial from "../../../components/KnowledgeHub/CaseStudy/Testimonial";
import CaseStudyLast from "../../../components/KnowledgeHub/CaseStudy/Last";
import CaseStudyClient from "../../../components/KnowledgeHub/CaseStudy/Client";
import CaseStudyTimeline from "../../../components/KnowledgeHub/CaseStudy/Timeline";
import Editor from "../../../utils/Editor/Instance";
import SeoInformation from "../../../components/KnowledgeHub/CaseStudy/Seo";
import { DOMAIN, STORAGE_DOMAIN } from "../../../env";

export default function NewAddCaseStudy() {
  const history = useHistory();
  const user = useContext(UserContext);
  const [data, setData] = useSetState({
    topic: { state: "", error: "" },
    link: { state: "", error: "" },
    hubSpotLink: { state: "", error: "" },
    postedDate: { state: "", error: "" },
    description: { state: "", error: "" },
    title: { state: "", error: "" },
    cardImage: {
      name: "No file chosen",
      state: null,
      error: "",
      link: "",
      src: [],
    },
    seoImage: {
      name: "No file chosen",
      state: null,
      error: "",
      link: "",
      src: [],
    },
  });
  const [content, setContent] = useSetState({
    hero: {
      title: "",
      description: "",
    },
    testimonial: {
      title: "",
      headling: "",
      description: "",
      person: "",
    },
    client: {
      location: "",
      companySize: "",
      industry: "",
    },
    timeline: {
      show: false,
      content: [],
    },
    last: {
      numberOfPages: "",
      image: {
        name: "No file chosen",
        state: null,
        error: false,
        link: "",
        src: [],
      },
    },
  });
  // eslint-disable-next-line
  const [editorInstance, setEditorInstance] = useState();
  // eslint-disable-next-line
  const [instanceModification, setInstanceModification] = useState(false);
  const [imagesUploaded, setImagesUploaded] = useState([]);

  const [current, setCurrent] = useState(0);

  const addError = (name, error) => {
    setData({
      [name]: {
        state: data[name].state,
        error: error,
      },
    });
  };

  const removeError = (name) => {
    setData({
      [name]: {
        state: data[name].state,
        error: "",
      },
    });
  };

  const checkField = (name) => {
    if (!data[name].state) {
      addError(name, "This field can not be empty!");
      return true;
    } else {
      removeError(name);
      return false;
    }
  };

  const validate = () => {
    let error = false;
    error = checkField("topic");
    error = checkField("link");
    error = checkField("hubSpotLink");
    error = checkField("postedDate");
    error = checkField("description");
    error = checkField("title");
    return error;
  };

  const onSave = async () => {
    const outputData = await editorInstance.save();

    if (validate()) return;

    const loadingMessage = message.loading("Loading...", 0);
    const blocks = outputData.blocks.map((block) => {
      if (block.type === "image") {
        let url = "";
        if (block.data.file.url.includes(STORAGE_DOMAIN))
          url = block.data.file.url.split(STORAGE_DOMAIN)[1];
        else url = block.data.file.url;
        return {
          ...block,
          data: {
            ...block.data,
            file: {
              ...block.data.file,
              url: url,
            },
          },
        };
      } else return block;
    });
    const formData = {
      topic: data.topic.state,
      link: data.link.state,
      hubSpotLink: data.hubSpotLink.state,
      postedDate: data.postedDate.state,
      description: data.description.state,
      title: data.title.state,
      sectionHero: JSON.stringify(content.hero),
      sectionTestimonial: JSON.stringify(content.testimonial),
      sectionClient: JSON.stringify(content.client),
      sectionTimeline: JSON.stringify(content.timeline),
      sectionLast: content.last.numberOfPages,
      sectionContent: JSON.stringify({ ...outputData, blocks }),
    };

    await axios
      .post(`${DOMAIN}/api/knowledgeHub/caseStudyNew/create`, formData, {
        headers: {
          "jwt-token": user.jwtToken,
        },
      })
      .then((response) => {
        message.success({
          content: `Case Study Saved!`,
          className: "text-center",
          duration: 2,
        });
        setInstanceModification(false);
        history.push(`/knowledge-hub/caseStudyNew/edit/${response.data.id}`);
      })
      .catch((error) => {
        message.error({
          content: `${error.response.data.error}`,
          className: "text-center",
          duration: 2,
        });
      })
      .finally(() => {
        loadingMessage();
      });
  };

  return (
    <div>
      <div className=" sticky top-16 z-10 flex flex-row flex-wrap items-center justify-center gap-5 border-b-2 bg-grey-4 p-3 md:top-0 md:justify-between">
        <p className=" mb-0 text-center font-poppins text-lg font-bold text-grey-1 md:text-2xl lg:text-left">
          New Case Study
        </p>
        <div className="flex justify-center gap-4">
          <div>
            <button
              type="button"
              onClick={() => onSave()}
              className={`flex cursor-pointer items-center justify-center gap-2 rounded-2xl border-2 border-green-11 bg-green-11 px-5 py-2 shadow-lg transition-all duration-500`}
            >
              <svg
                viewBox="0 0 24 24"
                className=" h-4 w-4 fill-current text-white"
              >
                <path d="M12,10a4,4,0,1,0,4,4A4,4,0,0,0,12,10Zm0,6a2,2,0,1,1,2-2A2,2,0,0,1,12,16Z" />
                <path d="M22.536,4.122,19.878,1.464A4.966,4.966,0,0,0,16.343,0H5A5.006,5.006,0,0,0,0,5V19a5.006,5.006,0,0,0,5,5H19a5.006,5.006,0,0,0,5-5V7.657A4.966,4.966,0,0,0,22.536,4.122ZM17,2.08V3a3,3,0,0,1-3,3H10A3,3,0,0,1,7,3V2h9.343A2.953,2.953,0,0,1,17,2.08ZM22,19a3,3,0,0,1-3,3H5a3,3,0,0,1-3-3V5A3,3,0,0,1,5,2V3a5.006,5.006,0,0,0,5,5h4a4.991,4.991,0,0,0,4.962-4.624l2.16,2.16A3.02,3.02,0,0,1,22,7.657Z" />
              </svg>
              <p className=" bg-grey1 font-montserrat font-bold uppercase text-white">
                Save
              </p>
            </button>
          </div>
          <button
            type="button"
            onClick={() => history.push("/knowledge-hub")}
            className="group flex cursor-pointer items-center justify-center gap-2 rounded-2xl border-2 border-red-600 px-5 py-2 shadow-lg transition-all duration-500 hover:bg-red-600"
          >
            <svg
              className=" h-4 w-4 fill-current text-red-600 transition-all duration-500 group-hover:text-white"
              viewBox="0 0 24 24"
            >
              <path d="M21,4H17.9A5.009,5.009,0,0,0,13,0H11A5.009,5.009,0,0,0,6.1,4H3A1,1,0,0,0,3,6H4V19a5.006,5.006,0,0,0,5,5h6a5.006,5.006,0,0,0,5-5V6h1a1,1,0,0,0,0-2ZM11,2h2a3.006,3.006,0,0,1,2.829,2H8.171A3.006,3.006,0,0,1,11,2Zm7,17a3,3,0,0,1-3,3H9a3,3,0,0,1-3-3V6H18Z" />
              <path d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18Z" />
              <path d="M14,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z" />
            </svg>

            <p className=" font-montserrat font-bold uppercase text-red-600 transition-all duration-500 group-hover:text-white">
              Cancel
            </p>
          </button>
        </div>
      </div>
      <div className="px-3 py-5">
        <div className="mx-auto flex max-w-5xl flex-col gap-5 md:flex-row">
          <div
            onClick={() => setCurrent(0)}
            className={`${
              current === 0
                ? "bg-green-11 text-white"
                : "bg-white text-green-11 hover:bg-green-11 hover:text-white"
            } flex w-full cursor-pointer flex-row items-center justify-center gap-4 rounded-xl border-2 border-green-11 px-6 py-3 shadow transition-colors duration-500`}
          >
            <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
              <g id="_01_align_center" data-name="01 align center">
                <path d="M12,0A12,12,0,1,0,24,12,12.013,12.013,0,0,0,12,0Zm8.647,7H17.426a19.676,19.676,0,0,0-2.821-4.644A10.031,10.031,0,0,1,20.647,7ZM16.5,12a10.211,10.211,0,0,1-.476,3H7.976A10.211,10.211,0,0,1,7.5,12a10.211,10.211,0,0,1,.476-3h8.048A10.211,10.211,0,0,1,16.5,12ZM8.778,17h6.444A19.614,19.614,0,0,1,12,21.588,19.57,19.57,0,0,1,8.778,17Zm0-10A19.614,19.614,0,0,1,12,2.412,19.57,19.57,0,0,1,15.222,7ZM9.4,2.356A19.676,19.676,0,0,0,6.574,7H3.353A10.031,10.031,0,0,1,9.4,2.356ZM2.461,9H5.9a12.016,12.016,0,0,0-.4,3,12.016,12.016,0,0,0,.4,3H2.461a9.992,9.992,0,0,1,0-6Zm.892,8H6.574A19.676,19.676,0,0,0,9.4,21.644,10.031,10.031,0,0,1,3.353,17Zm11.252,4.644A19.676,19.676,0,0,0,17.426,17h3.221A10.031,10.031,0,0,1,14.605,21.644ZM21.539,15H18.1a12.016,12.016,0,0,0,.4-3,12.016,12.016,0,0,0-.4-3h3.437a9.992,9.992,0,0,1,0,6Z" />
              </g>
            </svg>
            <p className="font-poppins text-xl font-bold uppercase">seo</p>
          </div>
          <div
            onClick={() => setCurrent(1)}
            className={`${
              current === 1
                ? "bg-green-11 text-white"
                : "bg-white text-green-11 hover:bg-green-11 hover:text-white"
            } flex w-full cursor-pointer flex-row items-center justify-center gap-4 rounded-xl border-2 border-green-11 px-6 py-3 shadow transition-colors duration-500`}
          >
            <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
              <path d="m19 1h-14a5.006 5.006 0 0 0 -5 5v12a5.006 5.006 0 0 0 5 5h14a5.006 5.006 0 0 0 5-5v-12a5.006 5.006 0 0 0 -5-5zm-14 2h14a3 3 0 0 1 3 3v1h-20v-1a3 3 0 0 1 3-3zm14 18h-14a3 3 0 0 1 -3-3v-9h20v9a3 3 0 0 1 -3 3zm0-8a1 1 0 0 1 -1 1h-12a1 1 0 0 1 0-2h12a1 1 0 0 1 1 1zm-4 4a1 1 0 0 1 -1 1h-8a1 1 0 0 1 0-2h8a1 1 0 0 1 1 1zm-12-12a1 1 0 1 1 1 1 1 1 0 0 1 -1-1zm3 0a1 1 0 1 1 1 1 1 1 0 0 1 -1-1zm3 0a1 1 0 1 1 1 1 1 1 0 0 1 -1-1z" />
            </svg>
            <p className="font-poppins text-xl font-bold uppercase">content</p>
          </div>
        </div>
        <div
          className={`${
            current === 0 ? "block" : "hidden"
          } mx-auto max-w-5xl py-5`}
        >
          <div className=" pb-10 text-center font-poppins text-3xl font-bold text-grey-1 md:text-4xl">
            <span className="text-green-11">SEO</span> Information
          </div>
          <SeoInformation
            id={undefined}
            user={user}
            data={data}
            setData={setData}
          />
        </div>
        <div className={`${current === 1 ? "block" : "hidden"} py-5`}>
          <div className=" pb-10 text-center font-poppins text-3xl font-bold text-grey-1 lg:text-4xl">
            <span className="text-green-11">Case Study</span> Content
          </div>
          <CaseStudyHero data={content.hero} setData={setContent} />
          <div className=" mx-auto flex w-full max-w-[1000px] flex-col gap-14 pt-14">
            <CaseStudyTestimonial
              data={content.testimonial}
              setData={setContent}
            />
            <Editor
              bodyData={{}}
              setEditorInstance={setEditorInstance}
              imagesUploaded={imagesUploaded}
              setImagesUploaded={setImagesUploaded}
              setEditorModification={setInstanceModification}
            />
            <CaseStudyTimeline data={content.timeline} setData={setContent} />
            <CaseStudyClient data={content.client} setData={setContent} />
            <CaseStudyLast
              id={undefined}
              data={content.last}
              setData={setContent}
              link={data.hubSpotLink.state}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
